import { useTranslation } from "react-i18next";
import { useDependency } from "../../../../../../contexts/DependencyProvider";
import React, { useEffect, useRef, useState } from "react";
import { ComboBox, Item } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { ListFPARequest2 } from "../../../../../../services/soap/project/requests/ListFPARequest2";
import { ListPaymentMethodRequest } from "../../../../../../services/soap/invoice/requests/ListPaymentMethodRequest";

interface PaymentMethod {
  id: string;
  name: string;
}

interface IPaymentMethodDropDown {
  selectedPaymentMethod: string;
  paymentMethodName?: string;
  onChange: (data: PaymentMethod | null) => void; // ✅ allow null when cleared
  isEdit?: any;
  isRequired?: any;
  errorMessage?: any;
  validationState?: any;
  width?: any;
}

const PaymentMethodDropDown: React.FC<IPaymentMethodDropDown> = ({
  selectedPaymentMethod,
  paymentMethodName,
  onChange,
  isEdit,
  isRequired,
  errorMessage,
  validationState,
  width,
}) => {
  const { t } = useTranslation("invoice");
  const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });

  const { store, invoiceService } = useDependency();

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");

  // keep your "initialLoad uses producerName" behavior
  const initialLoadRef = useRef(true);

  // prevent parent sync fighting while user types
  const isUserEditingRef = useRef(false);

  // if you still want "auto select 0th item" ONCE (only when not edit and no selectedProducer)
  const didInitialAutoSelectRef = useRef(false);

  const list = useAsyncList<PaymentMethod>({
    async load({ cursor, filterText }) {
      const offset = cursor ? Number(cursor) : 0;
      const limit = 5000;

      try {
        const response = await invoiceService.listPaymentMethod(
          new ListPaymentMethodRequest(store.Server, store.SessionId)
        );

        const folders: PaymentMethod[] = response?.ITEM
          ? response.ITEM.map((entry: any) => entry).filter(Boolean)
          : [];

        if (!Array.isArray(folders)) return { items: [] };

        // ✅ only mark initial load as complete AFTER first load
        if (initialLoadRef.current) {
          initialLoadRef.current = false;
        }

        return {
          items: folders,
          cursor: folders.length === limit ? String(offset + limit) : undefined,
        };
      } catch (error) {
        console.error("Error fetching producers:", error);
        return { items: [] };
      }
    },
  });

  // initial fetch
  useEffect(() => {
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ auto-select 0th item ONCE (only when not edit and nothing selected from parent)
  useEffect(() => {
    if (
      didInitialAutoSelectRef.current ||
      isEdit ||
      selectedPaymentMethod ||
      list.items.length === 0
    ) {
      return;
    }

    didInitialAutoSelectRef.current = true;

    const first = list.items[0];
    setSelectedKey(String(first.id));
    setInputValue(first.name ?? "");
    onChange(first);
  }, [list.items, isEdit, selectedPaymentMethod, onChange]);

  // ✅ sync from parent ONLY when user not typing
  useEffect(() => {
    if (isUserEditingRef.current) return;

    if (!selectedPaymentMethod) {
      setSelectedKey(null);
      // if you want input to show producerName when adding, keep it; otherwise empty:
      setInputValue("");
      return;
    }

    setSelectedKey(String(selectedPaymentMethod));
    const selected = list.items.find((x) => String(x.id) === String(selectedPaymentMethod));
    setInputValue(selected?.name ?? "");
  }, [selectedPaymentMethod, list.items]);

  const handleInputChange = (val: string) => {
    isUserEditingRef.current = true;

    setInputValue(val);
    list.setFilterText(val);

    // ✅ allow backspace: if text diverges from selected label, clear selection
    if (selectedKey) {
      const selected = list.items.find((x) => String(x.id) === String(selectedKey));
      const selectedLabel = selected?.name ?? "";
      if (val !== selectedLabel) {
        setSelectedKey(null);
      }
    }

    // ✅ cleared => empty dropdown
    if (!val.trim()) {
      setSelectedKey(null);
      list.setFilterText("");
      onChange(null);
    }
  };

  const handleSelectionChange = (key: React.Key | null) => {
    isUserEditingRef.current = false;

    const idStr = key ? String(key) : null;
    setSelectedKey(idStr);

    if (!idStr) return;

    const selected = list.items.find((x) => String(x.id) === idStr);
    if (selected) {
      setInputValue(selected.name ?? "");
      onChange(selected);
      list.setFilterText(""); // next focus shows all
    }
  };

  return (
    <ComboBox
      label={_t("payment_method")}
      aria-label={_t("payment_method")}
      items={list.items}
      selectedKey={selectedKey}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      loadingState={list.loadingState}
      onLoadMore={list.loadMore}
      onSelectionChange={handleSelectionChange}
      allowsCustomValue
      width={{ base: "100%", L: width }}
      isRequired={isRequired}
      validationState={validationState}
      errorMessage={errorMessage}
      onFocus={() => {
        isUserEditingRef.current = true;
        list.setFilterText(""); // ✅ show all values on click
      }}
      onBlur={() => {
        isUserEditingRef.current = false;
      }}
    >
      {(item) => (
        <Item key={item.id} textValue={item.name}>
          {item.name}
        </Item>
      )}
    </ComboBox>
  );
};

export default PaymentMethodDropDown;
